import { useEffect, useRef, useState } from 'react'
import styled, { css } from 'styled-components'

import { ILevelUpResult } from '../../components/types/Item'
import Star from '../../modules/ProductPage/UI/icons/star'
import { MAIN_COLORS, getRarityColors } from '../consts/colors'
import { bonusPop, shimmer, shimmerSweep, starPop } from '../consts/animations'
import MainButton from './UIKit/MainButton'

const RARITY_NAMES: Record<number, string> = {
  1: 'Обычная',
  2: 'Редкая',
  3: 'Эпическая',
  4: 'Мифическая',
  5: 'Легендарная',
}

interface ToyResultCardProps {
  name: string
  imageSrc: string
  rarity: number
  levelUpData: ILevelUpResult
  isLutyan: boolean
  reducedMotion: boolean
  onImageReady: () => void
  onContinue: () => void
  onShare?: () => void
  shareDisabled?: boolean
  isSharing?: boolean
}

const Shell = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px 14px 16px;
  overflow-y: auto;
`

const Certificate = styled.div<{ $accent: string; $shimmerOn: boolean }>`
  width: 100%;
  max-height: 680px;
  flex: none;
  margin-top: auto;
  padding: 28px 22px;
  border-radius: 16px;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 22px;
  background: ${MAIN_COLORS.gradient_purple_200};
  box-shadow: inset 0 0 0 1px ${p => p.$accent}59;

  ${p => p.$shimmerOn && css`
    &::after {
      content: '';
      position: absolute;
      inset: 0 auto 0 -55%;
      width: 50%;
      pointer-events: none;
      background: linear-gradient(90deg, transparent, ${p.$accent}22, transparent);
      animation: ${shimmerSweep} 2.5s ease-in-out infinite;
    }
  `}

  @media (prefers-reduced-motion: reduce) {
    &::after { animation: none; display: none; }
  }
`

const RarityWord = styled.div<{ $accent: string }>`
  color: ${p => p.$accent};
  font-family: 'Press Start 2P', 'ComplianceSans', monospace;
  font-size: 18px;
  line-height: 26px;
  font-weight: 400;
  text-transform: uppercase;
  text-align: center;
  text-shadow: 0 0 18px ${p => p.$accent}aa;
  z-index: 1;
`

const ToyImage = styled.img<{ $accent: string; $intense: boolean }>`
  width: min(54vw, 212px);
  height: min(54vw, 212px);
  flex: none;
  object-fit: contain;
  image-rendering: pixelated;
  filter: drop-shadow(0 0 30px ${p => p.$accent}${p => p.$intense ? 'aa' : '66'});
  z-index: 1;
`

const ToyName = styled.h1`
  max-width: 100%;
  color: ${MAIN_COLORS.yellow};
  font-family: 'ComplianceSans', sans-serif;
  font-size: 28px;
  line-height: 34px;
  font-weight: 400;
  text-align: center;
  overflow-wrap: anywhere;
  z-index: 1;
`

const Stars = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  z-index: 1;
`

const StarSlot = styled.div<{ $visible: boolean; $accent: string }>`
  width: 52px;
  height: 52px;
  flex: none;
  display: grid;
  place-items: center;
  opacity: ${p => p.$visible ? 1 : 0.32};
  filter: ${p => p.$visible ? `drop-shadow(0 0 14px ${p.$accent})` : 'none'};
  ${p => p.$visible && css`
    animation: ${starPop} .32s cubic-bezier(.34, 1.56, .64, 1) both;
  `}

  svg { width: 52px; height: 52px; }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

const BonusBadge = styled.div<{ $visible: boolean; $accent: string }>`
  width: 100%;
  padding: 18px 22px;
  border-radius: 14px;
  background: ${MAIN_COLORS.gradient_purple_100};
  box-shadow: inset 0 0 0 1px ${p => p.$accent}47;
  text-align: center;
  opacity: ${p => p.$visible ? 1 : 0};
  transform: ${p => p.$visible ? 'translateY(0) scale(1)' : 'translateY(10px) scale(.94)'};
  transition: opacity .3s ease-out, transform .42s cubic-bezier(.34, 1.56, .64, 1);
  z-index: 1;
  ${p => p.$visible && css`animation: ${bonusPop} .42s ease-out both;`}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transition: none;
  }
`

const BonusLabel = styled.div`
  color: ${MAIN_COLORS.purple_900};
  font-family: 'ComplianceSans', sans-serif;
  font-size: 16px;
  line-height: 22px;
`

const BonusAmount = styled.div<{ $primary: string; $secondary: string }>`
  color: ${p => p.$primary};
  font-family: 'Inter', 'ComplianceSans', sans-serif;
  font-size: 32px;
  line-height: 40px;
  font-weight: 700;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  background: linear-gradient(90deg, ${p => p.$primary}, ${p => p.$secondary});
  background-size: 200% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: ${shimmer} 2s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

const Hint = styled.div`
  color: ${MAIN_COLORS.purple_900};
  font-family: 'ComplianceSans', sans-serif;
  font-size: 15px;
  line-height: 20px;
  text-align: center;
  z-index: 1;
`

const Actions = styled.div`
  display: flex;
  gap: 8px;
  flex: none;
  margin-bottom: auto;

  > * { flex: 1; }

  > button {
    height: 56px;
    border-radius: 12px;
    padding: 12px 16px;
  }

  > button h2 {
    font-size: 17px;
    line-height: 22px;
    white-space: nowrap;
  }
`

const useAnimatedResult = (
  level: number,
  bonus: number,
  reducedMotion: boolean,
) => {
  const [stars, setStars] = useState(reducedMotion ? level : 0)
  const [bonusVisible, setBonusVisible] = useState(reducedMotion)
  const [bonusCount, setBonusCount] = useState(reducedMotion ? bonus : 0)
  const animationRef = useRef<number | null>(null)

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = []
    if (animationRef.current !== null) cancelAnimationFrame(animationRef.current)

    if (reducedMotion) {
      setStars(level)
      setBonusVisible(true)
      setBonusCount(bonus)
      return undefined
    }

    setStars(0)
    setBonusVisible(false)
    setBonusCount(0)
    const starDelay = 420
    const starStep = 220
    for (let index = 1; index <= level; index += 1) {
      timers.push(setTimeout(() => setStars(index), starDelay + (index - 1) * starStep))
    }

    const bonusAt = starDelay + level * starStep + 260
    timers.push(setTimeout(() => {
      setBonusVisible(true)
      const startedAt = performance.now()
      const duration = 700
      const frame = (now: number) => {
        const linear = Math.min(1, Math.max(0, (now - startedAt) / duration))
        const eased = 1 - Math.pow(1 - linear, 3)
        setBonusCount(Math.round(eased * bonus))
        if (linear < 1) animationRef.current = requestAnimationFrame(frame)
      }
      animationRef.current = requestAnimationFrame(frame)
    }, bonusAt))

    return () => {
      timers.forEach(clearTimeout)
      if (animationRef.current !== null) cancelAnimationFrame(animationRef.current)
    }
  }, [bonus, level, reducedMotion])

  return { stars, bonusVisible, bonusCount }
}

const ToyResultCard = ({
  name,
  imageSrc,
  rarity,
  levelUpData,
  isLutyan,
  reducedMotion,
  onImageReady,
  onContinue,
  onShare,
  shareDisabled = false,
  isSharing = false,
}: ToyResultCardProps) => {
  const normalizedRarity = Math.min(5, Math.max(1, Math.round(rarity || 1)))
  const colors = getRarityColors(normalizedRarity)
  const level = Math.min(5, Math.max(1, Math.round(levelUpData.level || 1)))
  const bonus = Math.max(0, levelUpData.bonus_added || 0)
  const { stars, bonusVisible, bonusCount } = useAnimatedResult(level, bonus, reducedMotion)

  const hint = levelUpData.is_max_level
    ? 'Эта фигурка прокачана на максимум!'
    : levelUpData.next_milestone_level && levelUpData.next_milestone_level > level
      ? `До бонуса: ещё ${levelUpData.next_milestone_level - level} lvl`
      : isLutyan
        ? 'Бонусный цифровой дроп к твоей капсуле'
        : 'Этот лутбол добавлен в вашу коллекцию'

  return (
    <Shell>
      <Certificate
        data-testid="toy-result-certificate"
        $accent={colors.primary}
        $shimmerOn={normalizedRarity >= 4 && !reducedMotion}
      >
        {normalizedRarity >= 2 && (
          <RarityWord $accent={colors.primary}>
            {RARITY_NAMES[normalizedRarity]}{normalizedRarity === 5 ? '!' : ''}
          </RarityWord>
        )}
        <ToyImage
          src={imageSrc}
          alt={name}
          $accent={colors.primary}
          $intense={normalizedRarity >= 3}
          onLoad={onImageReady}
          onError={onImageReady}
        />
        <ToyName>{name}</ToyName>
        <Stars aria-label={`Уровень ${level} из 5`}>
          {Array.from({ length: 5 }, (_, index) => (
            <StarSlot
              key={index}
              $visible={index < stars}
              $accent={colors.primary}
            >
              <Star filled={index < stars} />
            </StarSlot>
          ))}
        </Stars>
        <BonusBadge $visible={bonusVisible} $accent={colors.primary}>
          <BonusLabel>
            {levelUpData.is_max_level
              ? 'Максимальный уровень'
              : `Уровень ${level}`}
          </BonusLabel>
          <BonusAmount $primary={colors.primary} $secondary={colors.secondary}>
            + {bonusCount} Бонусов
          </BonusAmount>
        </BonusBadge>
        <Hint>{hint}</Hint>
      </Certificate>
      <Actions>
        {normalizedRarity >= 4 && onShare && (
          <MainButton
            copyButton
            onClick={onShare}
            style={{ opacity: shareDisabled ? .55 : 1, pointerEvents: shareDisabled ? 'none' : 'auto' }}
          >
            {isSharing ? 'Подготовка…' : 'Поделиться'}
          </MainButton>
        )}
        <MainButton onClick={onContinue}>Продолжить</MainButton>
      </Actions>
    </Shell>
  )
}

export default ToyResultCard
